/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { GenericIconEnum } from '../../atoms/index.js';
import { GenericIconSize } from '../../atoms/generic-icon/nn-generic-icon.enum';

/**
 * Default Modal used in Odil.
 *
 * @prop title The title of the modal.
 * @prop id Must be unique (auto-generated).
 * @slot openButton The button to open the modal.
 * @slot content The content inside the modal.
 * @slot footer The footer inside the modal.
 */
@customElement('nn-modal')
export class Modal extends LitElement {
  static styles = css`
    .nn-modal {
      align-items: center;
      backdrop-filter: blur(4px);
      background: rgba(0, 0, 0, 0.15);
      cursor: default;
      display: none;
      font-family: 'font-light';
      height: calc(100% - 60px);
      justify-content: center;
      padding: 30px 0;
      text-align: center;
      width: 100%;
      position: fixed;
      left: 0;
      top: 0;
      z-index: var(--nn-modal-z-index, 2000);
    }
    .nn-modal--lateral {
      /* @todo implement lateral behavior*/
    }

    .nn-modal__container {
      background-color: var(--nn-white, #fff);
      border-radius: 32px;
      border: 1px solid rgba(0, 0, 0, 0);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      height: calc(100vh - 60px);
      margin: auto;
      max-width: 786px;
      overflow: hidden;
    }
    .nn-modal__container--lateral {
      border-radius: 32px 0 0 32px;
      height: 100vh;
      position: absolute;
      right: 0;
      top: 0;
    }

    .nn-modal__content--close {
      cursor: pointer;
      display: inline-block;
      margin: 24px 32px 0 auto;
    }
    .nn-modal__content--custom-close {
      float: none;
      padding: 0;
      text-align: inherit;
      position: absolute;
      right: 0;
    }

    .nn-modal__custom-header {
      min-height: 30px;
    }

    .nn-modal__title {
      font-family: 'font-heavy';
      font-size: 40px;
      font-weight: 500;
      margin-top: 0;
      padding: 0 50px;
      text-align: left;
    }

    .nn-modal__content {
      height: 100vh;
      margin: 0 0 16px 0;
      overflow-x: auto;
      text-align: left;
    }

    .nn-modal__content.with-custom-header {
      padding-top: 152px;
    }

    .nn-modal--open {
      display: flex;
    }

    .nn-modal__footer {
      background: var(--nn-black-22, #f0f0f0);
      border-radius: 0 0 30px 30px;
      border: 0;
    }

    .custom-close {
      align-items: center;
      background: var(--nn-black-8, #f7f7f7);
      border: 0;
      border-radius: 50%;
      box-shadow: none;
      cursor: pointer;
      display: inline-flex;
      font-family: 'font-light';
      font-size: 2.5em;
      font-weight: 100;
      flex-wrap: wrap;
      height: 48px;
      justify-content: center;
      line-height: 0;
      margin: 0;
      opacity: 1;
      padding: 0;
      text-align: center;
      transition: 0.3s;
      width: 48px;
      position: absolute;
      right: 32px;
      top: 32px;
      z-index: 2;
    }
    .custom-close:before,
    .custom-close:after {
      position: absolute;
      left: 23px;
      content: ' ';
      height: 30px;
      width: 2px;
      background-color: var(--nn-grey-80, #323232);
    }
    .custom-close:before {
      transform: rotate(45deg);
    }
    .custom-close:after {
      transform: rotate(-45deg);
    }
    @media (max-width: 991px) {
      .nn-modal__container {
        max-width: 80%;
      }
    }
    @media (max-width: 767px) {
      .custom-close {
        height: 28px;
        width: 28px;
        right: 16px;
        top: 16px;
      }
      .custom-close:before,
      .custom-close:after {
        height: 18px;
        left: 13px;
      }
      .nn-modal {
        height: 100%;
        padding: 0;
      }
      .nn-modal__content.with-custom-header {
        padding-top: 115px;
      }
      .nn-modal__container {
        border-radius: 0;
        border: none;
        max-width: 100%;
        min-height: 100%;
      }
      .nn-modal__container--lateral {
        border-radius: 0;
        position: inherit;
        right: auto;
      }

      .nn-modal__content--close {
        margin-right: 12px;
        margin-top: 12px;
      }

      .nn-modal__title {
        line-height: 3rem;
        padding: 0 10px;
        margin-bottom: 20px;
      }
    }
  `;

  @property({ type: String }) mode = 'normal';

  @property({ type: String }) title = '';

  @property({ type: Boolean }) customHeader = false;

  @property({ type: String }) id = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: Boolean }) openState = false;

  @property({ type: Boolean }) disabled = false;

  @property({ type: Boolean }) stopPropagationInPopup = false;

  constructor() {
    super();
    this.addEventListener('click', (e: MouseEvent) => {
      if (undefined !== (e.target as HTMLElement).dataset.closeModal) {
        e.stopPropagation();
        this._dispatchClose(e);
      }
    });
  }

  renderTitle() {
    if (this.title) {
      return html`<p class="nn-modal__title">${this.title}</p>`;
    }
    return '';
  }

  renderCloseButton() {
    if (this.mode === 'normal') {
      return html`
        <nn-generic-icon
          iconName="${GenericIconEnum.CLOSE}"
          size="${GenericIconSize.MEDIUM}"
          class="nn-modal__content--close"
          @click="${this._dispatchClose}"
        ></nn-generic-icon>
      `;
    }
    if (this.mode === 'lateral') {
      return html`
        <button
          type="button"
          class="custom-close"
          aria-label="Close"
          @click="${this._dispatchClose}"
        ></button>
      `;
    }
    return '';
  }

  render() {
    return html`
      <slot name="openButton" @click="${this._dispatchOpen}"></slot>
      <div
        id="nn-modal-${this.id}"
        class="${classMap({
          'nn-modal': true,
          'nn-modal--open': this.openState,
          'nn-modal--close': !this.openState,
          'nn-modal--lateral': this.mode === 'lateral',
        })}"
        @click="${this.closeBackdrop}"
      >
        <div
          class="${classMap({
            'nn-modal__container': true,
            'nn-modal__container--lateral': this.mode === 'lateral',
          })}"
          data-close-modal
          @click="${this.onClickPopup}"
        >
          ${this.renderCloseButton()} ${this.renderTitle()}
          <slot
            id="custom-header"
            class="nn-modal__custom-header"
            name="custom-header"
          >
          </slot>
          <div
            class="nn-modal__content ${classMap({
              'with-custom-header': this.customHeader,
            })}"
          >
            <slot name="content"></slot>
          </div>
          <div class="nn-modal__footer">
            <slot class="nn-modal__footer" name="footer"></slot>
          </div>
        </div>
      </div>
    `;
  }

  disconnectedCallback() {
    //* Below super function MUST be called, DO NOT REMOVED!
    // https://lit.dev/docs/components/lifecycle/#custom-element-lifecycle
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
    this._dispatchClose();
  }

  _dispatchOpen(e: Event) {
    if (this.disabled) return;
    const htmlPage = document.querySelector('body');
    if (htmlPage) {
      htmlPage.style.overflow = 'hidden';
    }
    this.openState = true;
    e.preventDefault();
    const event = new CustomEvent('onOpen');
    this.dispatchEvent(event);
  }

  _dispatchClose(e?: Event) {
    const htmlPage = document.querySelector('body');
    this.openState = false;
    e?.preventDefault();
    e?.stopPropagation();
    const event = new CustomEvent('onClose');
    this.dispatchEvent(event);

    const modal = this.shadowRoot?.querySelector('.nn-modal');
    const modalContent = this.shadowRoot?.querySelector('.nn-modal__content');
    if (modal) {
      modal.scrollTop = 0;
    }
    if (modalContent) {
      modalContent.scrollTop = 0;
    }
    if (htmlPage) {
      htmlPage.style.overflow = 'scroll';
      const modals = htmlPage.querySelectorAll('nn-modal');

      let stillModalOpen = 0;
      modals.forEach(m => {
        const shadow = m.shadowRoot?.querySelector('.nn-modal--open');
        if (shadow && shadow.classList.contains('nn-modal--open')) {
          stillModalOpen += 1;
        }
      });
      // asynchronous issue: last modal just closed but still appears in 'nn-modal--open'
      if (stillModalOpen === 1 || (modal && modals.length === 0)) {
        htmlPage.removeAttribute('style');
      }
    }
  }

  closeBackdrop(e: Event) {
    const el = e.target as HTMLElement;
    if (el.classList.contains('nn-modal--open')) {
      this._dispatchClose(e);
    }
  }

  onClickPopup(e: Event) {
    if (this.stopPropagationInPopup) {
      e.stopPropagation();
    }
  }
}
